import { IS_PROD, API_BASE_URL } from '../config';

declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-X0PKXY6NFD';

// Determine environment based on backend domain and IS_PROD
const getEnvironment = (): 'production' | 'main' | 'local' => {
  if (IS_PROD && API_BASE_URL.includes('api-prod.nomadicml.com')) {
    return 'production';
  } else if (!IS_PROD && API_BASE_URL.includes('api.nomadicml.com')) {
    return 'main';
  } else {
    return 'local';
  }
};

const ENVIRONMENT = getEnvironment();

export const initGA = () => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('Google Analytics not loaded');
    return;
  }
  
  // Configure GA with environment-specific settings
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    custom_map: {
      'dimension1': 'environment',
      'dimension2': 'api_endpoint'
    },
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL
  });
  
  // Set custom dimensions immediately
  window.gtag('event', 'page_view', {
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL
  });
  
  //console.log(`Google Analytics initialized for environment: ${ENVIRONMENT}`);
};

export const pageview = (url: string, title?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: url,
    page_title: title || document.title,
    page_location: window.location.href,
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL,
  });
};

export const event = (action: string, parameters?: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', action, {
    ...parameters,
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL,
    send_to: GA_MEASUREMENT_ID,
  });
};

export const setUserId = (userId: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId,
  });
};

export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('set', 'user_properties', properties);
};

export const timing = (name: string, value: number, category?: string, label?: string) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'timing_complete', {
    name,
    value,
    event_category: category || 'Performance',
    event_label: label,
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL,
  });
};

export const exception = (description: string, fatal: boolean = false) => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'exception', {
    description,
    fatal,
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL,
  });
};

export const screenView = (screenName: string, appName: string = 'NomadicAI Platform') => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  window.gtag('event', 'screen_view', {
    app_name: `${appName} - ${ENVIRONMENT}`,
    screen_name: screenName,
    environment: ENVIRONMENT,
    api_endpoint: API_BASE_URL,
  });
};